"use client";

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { apiClient, API_BASE_URL } from '../api/client';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { FloorPriceHistoryModal } from '../components/FloorPriceHistoryModal';
import { PointsDistributionModal } from '../components/PointsDistributionModal';
import { Avatar } from '../ui/components/Avatar';
import { Button } from '../ui/components/Button';
import { TextField } from '../ui/components/TextField';
import { Badge } from '../ui/components/Badge';
import { Tabs } from '../ui/components/Tabs';
import { Table } from '../ui/components/Table';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { XamanTransactionModal, type TransactionStage } from '../components/transactions/XamanTransactionModal';
import { toSigningPayload } from '../lib/wallet';
import type { SigningPayload } from '../types/signing';
import type { TransactionFees, NFTDetails } from '../components/transactions/TransactionConfirmation';
import { useAuthStore } from '../store/authStore';
import { useNotificationStore } from '../store/notificationStore';
import { formatPrice, formatPriceRounded, formatFloorPrice, formatVolume, getRarityInfo, getRarityInfoInCategory } from '../utils/formatters';
import { captureMintReferral } from '../utils/mint-referral';
import { buildShareableUrl } from '../utils/referral';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { useNFTFilters, type SortOption, type OwnershipFilter, sortOptions, ownershipOptions, parseSortOption } from '../hooks/useNFTFilters';
import { NFTFilterBar } from '../components/nft/NFTFilterBar';
import { useSweepPurchase } from '../hooks/useSweepPurchase';
import { SweepModeBar } from '../components/sweep/SweepModeBar';
import { ContestBanner } from '../components/sweepstakes/ContestBanner';
import { ResultsBanner } from '../components/sweepstakes/ResultsBanner';
import { useCollectionSweepstakes, useEligibleOffers } from '../hooks/useSweepstakes';
import { useLaunchMintRewards } from '../hooks/useMintRewards';
import { SweepProgressModal } from '../components/sweep/SweepProgressModal';
import { VoteBoardTierBadge, getVoteBoardUrl } from '../components/collection/VoteBoardTierBadge';
import { formatDistanceToNow } from 'date-fns';
import * as SubframeCore from '@subframe/core';
import {
  FeatherVerified,
  FeatherTwitter,
  FeatherGlobe,
  FeatherSlidersHorizontal,
  FeatherChevronDown,
  FeatherSearch,
  FeatherX,
  FeatherChevronRight,
  FeatherBarChart2,
  FeatherBell,
  FeatherPlus,
  FeatherStar,
  FeatherFlag,
  FeatherDollarSign,
  FeatherImage,
  FeatherActivity,
  FeatherAward,
  FeatherShare,
  FeatherExternalLink,
  FeatherCheckSquare,
  FeatherZap,
  FeatherDownload,
} from '@subframe/core';

// Custom broom icon for sweep feature
const BroomIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {/* Broom handle */}
    <line x1="4" y1="4" x2="12" y2="12" />
    {/* Broom head bristles */}
    <path d="M12 12 L20 20 M12 12 L18 22 M12 12 L22 18 M12 12 L16 21 M12 12 L21 16" />
  </svg>
);
import { IconButton } from '../ui/components/IconButton';
import { DropdownMenu } from '../ui/components/DropdownMenu';
import { submitCollectionReport, type CollectionReportType } from '../api/reports';
import { AlertConfigModal } from '../components/alerts/AlertConfigModal';
import { AlertConfigList } from '../components/alerts/AlertConfigList';
import { NFTCard } from '../components/nft/NFTCard';
import { useAlertConfigsByCollection } from '../hooks/useAlertConfigs';
import type { NotificationType, AlertConfig } from '../api/alertConfigs';
import { CreateCollectionOfferPopover } from '../components/collection-offers/CreateCollectionOfferPopover';
import { CollectionOffersList } from '../components/collection-offers/CollectionOffersList';
import { AcceptCollectionOfferModal } from '../components/collection-offers/AcceptCollectionOfferModal';
import {
  getCollectionOffers,
  type CollectionOffer,
  type CollectionOfferStats,
} from '../api/collectionOffers';
import { CollectionChat } from '../components/collection-chat/CollectionChat';
import { useCollectionChatUnreadCount, useMarkCollectionChatAsRead } from '../hooks/useCollectionChat';
import { BundlesTab } from '../components/bundles';
import type { SelectableNFT } from '../components/bundles';
import type { Offer } from '../types';
import { useCartStore, type CartItem } from '../store/cartStore';
import { featureFlags } from '../config/featureFlags';
import { RichlistTable } from '../components/collection/RichlistTable';
import { getCollectionRichlist, getCollectionPointsDistribution, type RichlistResult, type PointsDistributionResult } from '../api/richlist';
import {
  calculateBuyAmount,
  calculateMarketplaceFee,
  getMarketplaceFeePercentage,
  getMarketplaceName,
} from '../lib/marketplace-constants';
import { MintSection, type ActiveLaunchForMint } from '../components/mint';
import { AmbassadorMintBanner } from '../components/mint/AmbassadorMintBanner';
import { useMint } from '../hooks/useMint';
import { checkEligibility as checkMintEligibility, requestMint as requestMintApi, getDropEditions, type DropEdition } from '../api/drops';
import { submitXPost, getMyActiveAllocations } from '../api/ambassadors';
import { getCollectionCountdown } from '../api/mutations';

interface Collection {
  id: string;
  name: string;
  description: string;
  image_url?: string;
  floor_price?: number;
  total_supply: number;
  volume_all_time: number;
  volume_24h: number;
  is_verified: boolean;
  is_featured: boolean;
  has_points_system?: boolean;
  preferred_marketplace?: string;
  use_original_media?: boolean;
  creator_address?: string;
  taxon?: number;
  rarity_methodology?: string;
  skip_trait_stats?: boolean;
  mint_url?: string | null;
  project_id?: string | null;
  project_name?: string | null;
  project_slug?: string | null;
  project_image_url?: string | null;
  external_url?: string | null;
  social_links?: {
    twitter?: string;
    website?: string;
  };
  statistics: {
    totalSupply: number;
    owners: number;
    listedItems: number;
    floorPrice?: number;
    volume24h: number;
    volume7d: number;
    volume30d: number;
    volumeTotal: number;
    averagePrice?: number;
  };
  traits: any[];
  trait_stats?: {
    [traitType: string]: {
      [value: string]: {
        count: number;
        percentage: number;
      };
    };
  };
  xrpl_voteboard_score?: number | null;
  xrpl_voteboard_data?: {
    slug?: string;
    trustTier?: { tier: string; label: string; color: string } | null;
    currentPosition?: number | null;
    movement?: { direction?: string; positions?: number } | null;
    scoring?: { uniqueVoters?: number; totalVotes?: number; vouchCount?: number } | null;
  } | null;
  // Active launch for mint UI
  activeLaunch?: {
    launch_id: string;
    launch_slug: string;
    launch_status: string;
    collection_type?: string;
    mint_phase: 'allowlist' | 'allowlist_2' | 'public' | null;
    allowlist_enabled: boolean;
    allowlist_2_enabled: boolean;
    allowlist_start_at: string | null;
    allowlist_2_start_at: string | null;
    public_start_at: string | null;
    end_at: string | null;
    mint_price_drops: number | null;
    allowlist_mint_price_drops: number | null;
    allowlist_2_mint_price_drops: number | null;
    launch_total_supply: number | null;
    minted_count: number;
    transfer_fee: number;
    limit_per_wallet: number | null;
    service_fee_percent?: number;
    compose_enabled?: boolean;
    composition_fee_per_nft_drops?: number;
    referral_enabled?: boolean;
    referral_bps?: number | null;
    userAllowlistStatus?: {
      isOnAllowlist: boolean;
      isOnAllowlist2: boolean;
      mintLimit?: number;
      mintedCount?: number;
      mintLimit2?: number;
      mintedCount2?: number;
    } | null;
    eligibility?: {
      eligible: boolean;
      reason?: string;
      mintPhase: 'allowlist' | 'allowlist_2' | 'public' | null;
      priceDrops: number;
      maxAllowed: number;
    } | null;
    availableCount?: number;
  } | null;
}

interface NFT {
  id: string;
  token_id: string;
  nft_token_id: string;
  name: string;
  description: string;
  image_url?: string;
  thumbnail_url?: string;
  media_url?: string;
  collection_id: string;
  owner_address: string;
  last_sale_price?: number;
  min_sell_price?: number;
  rarity_score?: number;
  rarity_rank?: number;
  collection_points?: number | null;
  traits: any[];
  is_promoted?: boolean;
  promotion_expires_at?: string;
  active_offers?: Offer[];
}

interface Transaction {
  id: string;
  hash: string;
  transaction_type: string;
  amount: number | null;
  from_address: string;
  to_address: string;
  occurred_at: string;
  event: 'Sale' | 'Transfer';
  nft: {
    nft_id: string;
    name: string;
    thumbnail_url?: string;
    image_url?: string;
  };
}

type TransactionTypeFilter = 'sales' | 'transfers' | 'all';

/** Countdown banner for scheduled mutation batches */
function MutationCountdownBanner({ message, scheduledAt, totalCount }: { message?: string; scheduledAt: string; totalCount?: number }) {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const update = () => {
      const diff = new Date(scheduledAt).getTime() - Date.now();
      if (diff <= 0) {
        setTimeLeft('now');
        return;
      }
      const hours = Math.floor(diff / 3_600_000);
      const minutes = Math.floor((diff % 3_600_000) / 60_000);
      const seconds = Math.floor((diff % 60_000) / 1_000);
      const parts: string[] = [];
      if (hours > 0) parts.push(`${hours}h`);
      if (minutes > 0 || hours > 0) parts.push(`${minutes}m`);
      parts.push(`${seconds}s`);
      setTimeLeft(parts.join(' '));
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [scheduledAt]);

  return (
    <div className="w-full border border-brand-600 rounded-md px-4 py-3 bg-default-background mutation-countdown-glow">
      <style>{`
        @keyframes mutation-countdown-glow {
          0%, 100% { box-shadow: 0 0 6px rgba(0,200,200,0.2), inset 0 0 6px rgba(0,200,200,0.05); }
          50% { box-shadow: 0 0 20px rgba(0,200,200,0.5), inset 0 0 10px rgba(0,200,200,0.08); }
        }
        .mutation-countdown-glow { animation: mutation-countdown-glow 2s ease-in-out infinite; }
      `}</style>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          <FeatherZap className="h-4 w-4 text-brand-600" />
          <span className="text-body font-body text-default-font font-semibold">
            {message || 'NFT Update Incoming'}
          </span>
          <span className="text-caption font-caption text-subtext-color">
            {totalCount ? `${totalCount} dynamic NFTs will be mutated` : 'Dynamic NFT mutation scheduled'}
          </span>
        </div>
        <span className="text-body font-body text-default-font font-mono shrink-0">
          {timeLeft === 'now' ? 'Happening now!' : timeLeft}
        </span>
      </div>
    </div>
  );
}

export const CollectionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // NFT filter state (sort, price, rarity, points, traits, ownership, search)
  // filterVersion increments on each Apply/Clear to trigger the load useEffect
  // after React commits the new state (avoids stale closure reads from buildApiParams)
  const [filterVersion, setFilterVersion] = useState(0);

  const [collection, setCollection] = useState<Collection | null>(null);
  // Points distribution — preloaded so we can decide whether to show the
  // "View Points Distribution" link, the Best Value sort option, and Pts/XRP on cards.
  // Uniform-points collections (distribution.length <= 1) get none of these.
  const [pointsDistribution, setPointsDistribution] = useState<PointsDistributionResult | null>(null);

  // "Has meaningfully variable points" — true only when the collection has a points
  // system AND at least two distinct point tiers. Drives defaultSort and the
  // conditional Pts/XRP / Best-Value UI. Falls back to false until the distribution
  // has loaded, which is why the NFT fetch below waits for `pointsReady`.
  const hasVariablePoints = !!(
    collection?.has_points_system &&
    pointsDistribution &&
    pointsDistribution.distribution.length > 1
  );

  const filters = useNFTFilters({
    defaultSort: hasVariablePoints ? 'best-value' : 'price-low-high',
    onApply: () => setFilterVersion(v => v + 1),
  });
  const {
    sortOption, minPrice, maxPrice, minRarity, maxRarity,
    minPoints, maxPoints, searchQuery, selectedTraits, ownershipFilter,
    expandedTraits, toggleTraitExpanded,
    currentPage, setCurrentPage,
  } = filters;

  const [nfts, setNfts] = useState<NFT[]>([]);
  const [loading, setLoading] = useState(true);
  const [nftsLoading, setNftsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [csvDownloading, setCsvDownloading] = useState(false);
  // Store the broker address and offer ID from the offer when user clicks Buy Now
  const [currentBrokerAddress, setCurrentBrokerAddress] = useState<string | null>(null);
  const [currentOfferIndex, setCurrentOfferIndex] = useState<string | null>(null);

  // Mutation countdown state
  const [mutationCountdown, setMutationCountdown] = useState<{ message?: string; scheduledAt?: string; totalCount?: number } | null>(null);

  // Sweepstakes contest state
  const { contestView: sweepstakesContest } = useCollectionSweepstakes(collection?.id);
  const activeContestId = sweepstakesContest?.contest?.status === 'active' && sweepstakesContest?.contest?.contest_type !== 'mint_rewards'
    ? sweepstakesContest?.contest?.id : undefined;
  const { eligibleOfferIds } = useEligibleOffers(activeContestId);

  // Mint rewards contest state (for collections with active launches)
  const activeLaunchId = collection?.activeLaunch?.launch_id;
  const { contestView: mintRewardsContest } = useLaunchMintRewards(activeLaunchId);
  const [contestEligibleFilter, setContestEligibleFilter] = useState(
    () => searchParams.get('contestEligible') === 'true'
  );

  // Buy Now transaction modal state
  const { user, isAuthenticated } = useAuthStore();
  const { success: showSuccess, error: showError } = useNotificationStore();
  const cartStore = useCartStore();
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [transactionStage, setTransactionStage] = useState<TransactionStage>('confirmation');
  const [transactionFees, setTransactionFees] = useState<TransactionFees | null>(null);
  const [transactionNFTDetails, setTransactionNFTDetails] = useState<NFTDetails | null>(null);
  const [xamanPayload, setXamanPayload] = useState<SigningPayload | null>(null);
  const [txHash, setTxHash] = useState<string | undefined>(undefined);
  const [processing, setProcessing] = useState(false);

  // Floor price history modal state
  const [showFloorPriceModal, setShowFloorPriceModal] = useState(false);

  // Mint state for collections with active launches
  const hasActiveLaunch = !!(collection?.activeLaunch &&
    ['ready', 'active'].includes(collection.activeLaunch.launch_status));
  const isMintingLive = !!(collection?.activeLaunch?.launch_status === 'active' &&
    collection?.activeLaunch?.mint_phase);
  const launchSlug = collection?.activeLaunch?.launch_slug || '';

  // Capture mint referral code from URL params (e.g., ?mref=CODE)
  useEffect(() => {
    const mrefCode = searchParams.get('mref');
    if (mrefCode && activeLaunchId) {
      captureMintReferral(activeLaunchId, mrefCode);
      // Strip mref from URL without reload
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('mref');
      setSearchParams(newParams, { replace: true });
    }
  }, [activeLaunchId, searchParams, setSearchParams]);

  // Edition drops: load the artwork gallery so buyers can pick a piece
  const isEditionLaunch = collection?.activeLaunch?.collection_type === 'edition';
  const [dropEditions, setDropEditions] = useState<DropEdition[] | null>(null);
  const loadEditions = useCallback(async () => {
    if (!isEditionLaunch || !launchSlug) return;
    try {
      const editions = await getDropEditions(launchSlug);
      setDropEditions(editions);
    } catch (err) {
      console.error('Failed to load editions:', err);
    }
  }, [isEditionLaunch, launchSlug]);

  useEffect(() => {
    loadEditions();
  }, [loadEditions]);

  const mint = useMint({
    launchSlug,
    launchId: activeLaunchId || undefined,
    onMintSuccess: () => {
      // Refresh collection data after successful mint
      loadCollection();
      loadEditions();
    },
    onRefresh: () => {
      loadCollection();
      loadEditions();
    },
  });

  // Ambassador mint state
  const [ambassadorAllocation, setAmbassadorAllocation] = useState<{
    id: string;
    status: string;
    claimed_at: string | null;
    x_post_url: string | null;
    x_post_deadline_at: string | null;
    lock_expires_at: string | null;
    expires_at: string | null;
  } | null>(null);
  const [ambassadorClaimLoading, setAmbassadorClaimLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !activeLaunchId) return;
    getMyActiveAllocations().then(res => {
      if (res?.success) {
        const alloc = (res.data as any[]).find((a: any) => a.launch_id === activeLaunchId);
        if (alloc) setAmbassadorAllocation(alloc);
      }
    }).catch(() => {});
  }, [isAuthenticated, activeLaunchId]);

  async function handleAmbassadorClaim(allocationId: string) {
    if (!launchSlug) return;
    setAmbassadorClaimLoading(true);
    try {
      await requestMintApi(launchSlug, 1, null, allocationId);
      // Refresh allocation state
      const res = await getMyActiveAllocations();
      if (res?.success) {
        const alloc = (res.data as any[]).find((a: any) => a.launch_id === activeLaunchId);
        if (alloc) setAmbassadorAllocation(alloc);
        else setAmbassadorAllocation(prev => prev ? { ...prev, status: 'claimed', claimed_at: new Date().toISOString() } : null);
      }
      loadCollection();
    } catch (err: any) {
      useNotificationStore.getState().error(err?.response?.data?.message || err?.message || 'Failed to claim ambassador mint');
    } finally {
      setAmbassadorClaimLoading(false);
    }
  }

  async function handleSubmitAmbassadorXPost(allocationId: string, url: string) {
    await submitXPost(allocationId, url);
    setAmbassadorAllocation(prev => prev ? { ...prev, x_post_url: url } : null);
  }

  // Tab and activity state
  const initActiveTab = (() => {
    const tab = searchParams.get('tab');
    if (tab === 'activity') return 'activity';
    if (tab === 'richlist') return 'richlist';
    if (tab === 'alerts') return 'alerts';
    if (tab === 'offers') return 'offers';
    if (tab === 'bundles') return 'bundles';
    if (tab === 'traits') return 'traits';
    if (tab === 'chat') return 'chat';
    if (tab === 'compose') return 'compose';
    return 'nfts';
  })();

  const initTransactionTypeFilter = (() => {
    const type = searchParams.get('type') as TransactionTypeFilter;
    return type === 'sales' || type === 'transfers' || type === 'all' ? type : 'sales';
  })();

  const initActivitySelectedTraits = (() => {
    const traitsParam = searchParams.get('activityTraits');
    if (traitsParam) {
      try {
        const parsed = JSON.parse(traitsParam);
        if (typeof parsed === 'object' && parsed !== null) {
          return parsed;
        }
      } catch (err) {
        console.warn('Failed to parse activityTraits from URL');
      }
    }
    return {};
  })();

  const [activeTab, setActiveTab] = useState<'nfts' | 'activity' | 'richlist' | 'alerts' | 'offers' | 'bundles' | 'traits' | 'chat' | 'compose'>(initActiveTab);
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(initActiveTab === 'chat'); // Auto-collapse on chat tab
  // A dead image_url (e.g. an IPFS gateway that can't find the CID) should degrade
  // to the initial-letter placeholder instead of the browser's broken-image alt text.
  const [profileImageFailed, setProfileImageFailed] = useState(false);
  useEffect(() => {
    setProfileImageFailed(false);
  }, [collection?.image_url]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionTotalPages, setTransactionTotalPages] = useState(1);
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<TransactionTypeFilter>(initTransactionTypeFilter);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [activitySelectedTraits, setActivitySelectedTraits] = useState<{ [key: string]: string[] }>(initActivitySelectedTraits);
  const [showActivityFilters, setShowActivityFilters] = useState(false);

  // Richlist tab state
  const [richlist, setRichlist] = useState<RichlistResult | null>(null);
  const [richlistLoading, setRichlistLoading] = useState(false);

  // Points distribution modal — preloaded data is declared above useNFTFilters since
  // `defaultSort` depends on whether points are meaningfully variable.
  const [showPointsDistribution, setShowPointsDistribution] = useState(false);

  // Traits tab state
  const [traitThumbnails, setTraitThumbnails] = useState<{
    [traitType: string]: {
      [value: string]: {
        nftTokenId: string;
        thumbnailUrl: string | null;
        imageUrl: string | null;
        rarityRank: number | null;
        floorPrice: number | null;
      };
    };
  }>({});
  const [traitThumbnailsLoading, setTraitThumbnailsLoading] = useState(false);
  const [selectedRarityTiers, setSelectedRarityTiers] = useState<Set<string>>(new Set());

  // Sweep mode state
  const [isSweepMode, setIsSweepMode] = useState(false);
  const [sweepCount, setSweepCount] = useState(1);
  const [showSweepProgress, setShowSweepProgress] = useState(false);
  const sweepPurchase = useSweepPurchase();

  // Sweepable NFTs - only listed NFTs that user doesn't own
  const sweepableNfts = useMemo(() => {
    return nfts.filter(nft =>
      nft.min_sell_price != null &&
      nft.min_sell_price > 0 &&
      nft.owner_address !== user?.xrpl_address &&
      nft.active_offers && nft.active_offers.length > 0
    );
  }, [nfts, user?.xrpl_address]);

  // Selected NFTs for sweep (first N from sweepable list)
  const selectedForSweep = useMemo(() => {
    return sweepableNfts.slice(0, sweepCount);
  }, [sweepableNfts, sweepCount]);

  // Handlers for sweep mode
  const enterSweepMode = () => {
    setIsSweepMode(true);
    // Set initial sweep count to 1
    setSweepCount(1);
  };

  const exitSweepMode = () => {
    setIsSweepMode(false);
    setSweepCount(1);
  };

  const handleSweepContinue = () => {
    if (selectedForSweep.length > 0) {
      // Need to cast to the NFT type expected by sweep hook
      sweepPurchase.startConfirmation(selectedForSweep as any);
      setShowSweepProgress(true);
    }
  };

  const handleSweepComplete = () => {
    setShowSweepProgress(false);
    setIsSweepMode(false);
    sweepPurchase.reset();
    // Refresh NFTs to reflect purchases
    loadNFTs();
  };

  // Tabs scroll indicator
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollTabs, setCanScrollTabs] = useState(false);

  // Check if tabs need scrolling (only show indicator if significant overflow)
  const checkTabsOverflow = useCallback(() => {
    const container = tabsContainerRef.current;
    if (container) {
      const overflow = container.scrollWidth - container.clientWidth;
      const hasScrolledToEnd = container.scrollLeft >= overflow - 10;
      // Only show if there's meaningful overflow (30px+) AND user hasn't scrolled to end
      setCanScrollTabs(overflow > 30 && !hasScrolledToEnd);
    }
  }, []);

  // Check overflow on mount and resize
  useEffect(() => {
    checkTabsOverflow();
    window.addEventListener('resize', checkTabsOverflow);
    return () => window.removeEventListener('resize', checkTabsOverflow);
  }, [checkTabsOverflow]);

  useEffect(() => {
    if (id) {
      loadCollection();
      // Fetch mutation countdown
      getCollectionCountdown(id).then(data => {
        setMutationCountdown(data.active ? data : null);
      }).catch(() => {});
    }
  }, [id]);

  // Gate the NFT fetch on the collection being loaded AND (for points collections)
  // the distribution having loaded. Until both arrive we can't determine whether
  // points are meaningfully variable, which drives `defaultSort`. Firing earlier
  // would send a request with the wrong sort that can race with — and overwrite —
  // the correct one fired after `defaultSort` updates.
  const pointsReady = !collection?.has_points_system || pointsDistribution !== null;
  useEffect(() => {
    if (id && collection?.id && pointsReady) {
      loadNFTs();
    }
  }, [id, collection?.id, pointsReady, currentPage, sortOption, selectedTraits, ownershipFilter, filterVersion]);

  useEffect(() => {
    if (id && activeTab === 'activity') {
      loadTransactions();
    }
  }, [id, activeTab, transactionPage, transactionTypeFilter, activitySelectedTraits]);

  // Load trait thumbnails when traits tab is active
  useEffect(() => {
    if (collection?.id && activeTab === 'traits' && Object.keys(traitThumbnails).length === 0) {
      loadTraitThumbnails();
    }
  }, [collection?.id, activeTab]);

  // Load richlist when richlist tab is active
  useEffect(() => {
    if (collection?.id && activeTab === 'richlist' && !richlist) {
      const loadRichlist = async () => {
        setRichlistLoading(true);
        try {
          const data = await getCollectionRichlist(collection.id);
          setRichlist(data);
        } catch (err) {
          console.error('Failed to load richlist:', err);
        } finally {
          setRichlistLoading(false);
        }
      };
      loadRichlist();
    }
  }, [collection?.id, activeTab, richlist]);

  // Prefetch points distribution so we can decide whether to show the
  // "View Points Distribution" link. Only useful if there's more than one
  // tier — uniform strategies (all NFTs same points) produce a flat bar,
  // so we suppress the link in that case.
  useEffect(() => {
    if (!collection?.id || !collection.has_points_system) {
      setPointsDistribution(null);
      return;
    }
    let cancelled = false;
    getCollectionPointsDistribution(collection.id)
      .then(data => { if (!cancelled) setPointsDistribution(data); })
      .catch(err => { console.error('Failed to load points distribution:', err); });
    return () => { cancelled = true; };
  }, [collection?.id, collection?.has_points_system]);

  // Load collection offer stats for badge (only for authenticated users)
  useEffect(() => {
    if (collection?.id && isAuthenticated) {
      loadCollectionOfferStats();
    }
  }, [collection?.id, isAuthenticated]);

  // Load user's NFTs when bundles tab is active (for bundle creation)
  useEffect(() => {
    if (collection?.id && isAuthenticated && user?.xrpl_address && activeTab === 'bundles' && userNftsForAccept.length === 0) {
      const loadUserNftsForBundles = async () => {
        setLoadingUserNfts(true);
        try {
          const response = await apiClient.get<any>(
            `/collections/${collection.id}/nfts?owner=${user.xrpl_address}&limit=100`
          );
          setUserNftsForAccept(response.data?.nfts || []);
        } catch (err) {
          console.error('Failed to fetch user NFTs for bundles:', err);
        } finally {
          setLoadingUserNfts(false);
        }
      };
      loadUserNftsForBundles();
    }
  }, [collection?.id, isAuthenticated, user?.xrpl_address, activeTab]);

  // Sync activeTab with URL searchParams when they change (e.g., from clicking trait links)
  useEffect(() => {
    const tab = searchParams.get('tab');
    let newTab: 'nfts' | 'activity' | 'richlist' | 'alerts' | 'offers' | 'bundles' | 'traits' | 'chat' | 'compose' = 'nfts';
    if (tab === 'activity') newTab = 'activity';
    else if (tab === 'richlist') newTab = 'richlist';
    else if (tab === 'alerts') newTab = 'alerts';
    else if (tab === 'offers') newTab = 'offers';
    else if (tab === 'bundles') newTab = 'bundles';
    else if (tab === 'traits') newTab = 'traits';
    else if (tab === 'chat') newTab = 'chat';
    else if (tab === 'compose') newTab = 'compose';

    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [searchParams]);

  const loadCollectionOfferStats = async () => {
    if (!collection?.id) return;
    try {
      const result = await getCollectionOffers(collection.id, { limit: 1 });
      setCollectionOfferStats(result.stats);
    } catch (err) {
      console.error('Error loading collection offer stats:', err);
    }
  };

  // Alert configuration state
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [selectedAlertType, setSelectedAlertType] = useState<NotificationType | null>(null);
  const [editingAlertConfig, setEditingAlertConfig] = useState<AlertConfig | null>(null);

  // Report issue state
  const [reportDropdownOpen, setReportDropdownOpen] = useState(false);
  const [submittingReport, setSubmittingReport] = useState(false);

  // Collection offer modal state
  const [showCollectionOfferModal, setShowCollectionOfferModal] = useState(false);
  const [collectionOffersRefreshKey, setCollectionOffersRefreshKey] = useState(0);

  // Collection offers state (for tab badge and accept flow)
  const [collectionOfferStats, setCollectionOfferStats] = useState<CollectionOfferStats | null>(null);
  const [showAcceptOfferModal, setShowAcceptOfferModal] = useState(false);
  const [selectedOfferToAccept, setSelectedOfferToAccept] = useState<CollectionOffer | null>(null);

  // User's NFTs in this collection (for accept offer flow)
  // Note: userOwnedNfts from paginated nfts may not include all user NFTs
  // We use this for quick UI checks, but fetch all when opening modal
  const userOwnedNfts = nfts.filter(nft => nft.owner_address === user?.xrpl_address);
  const userOwnsNftsInCollection = userOwnedNfts.length > 0;

  // State for user's full NFT list in this collection (fetched on demand)
  const [userNftsForAccept, setUserNftsForAccept] = useState<NFT[]>([]);
  const [loadingUserNfts, setLoadingUserNfts] = useState(false);

  // Collection chat unread count (for tab badge)
  // Always enabled so invalidateQueries works, but only poll when NOT on chat tab
  const { unreadCount: chatUnreadCount } = useCollectionChatUnreadCount(collection?.id, {
    enabled: true,
    pollingInterval: activeTab !== 'chat' ? 30000 : 0, // Only poll when not on chat tab
  });
  const markChatAsReadMutation = useMarkCollectionChatAsRead();

  const handleAcceptOffer = async (offer: CollectionOffer) => {
    if (!user?.xrpl_address || !collection?.id) return;

    setSelectedOfferToAccept(offer);
    setLoadingUserNfts(true);

    try {
      // Fetch all user's NFTs in this collection (not limited by pagination)
      const response = await apiClient.get<any>(
        `/collections/${collection.id}/nfts?owner=${user.xrpl_address}&limit=100`
      );
      setUserNftsForAccept(response.data?.nfts || []);
    } catch (err) {
      console.error('Failed to fetch user NFTs:', err);
      // Fall back to what we have from paginated results
      setUserNftsForAccept(userOwnedNfts);
    } finally {
      setLoadingUserNfts(false);
      setShowAcceptOfferModal(true);
    }
  };

  const handleAcceptOfferSuccess = () => {
    setShowAcceptOfferModal(false);
    setSelectedOfferToAccept(null);
    // Refresh the collection offer stats and NFT list
    loadCollectionOfferStats();
    loadNFTs();
  };

  const handleCreateAlert = (notificationType: NotificationType) => {
    setSelectedAlertType(notificationType);
    setEditingAlertConfig(null); // Clear any existing config for create mode
    setShowAlertModal(true);
  };

  const handleEditAlert = (config: AlertConfig) => {
    setSelectedAlertType(config.notification_type);
    setEditingAlertConfig(config);
    setShowAlertModal(true);
  };

  const handleCloseAlertModal = () => {
    setShowAlertModal(false);
    setSelectedAlertType(null);
    setEditingAlertConfig(null);
  };

  const handleReport = async (reportType: CollectionReportType) => {
    if (!collection?.id) return;

    if (!isAuthenticated) {
      showError('Please sign in to report issues');
      return;
    }

    setSubmittingReport(true);
    try {
      await submitCollectionReport(collection.id, reportType);
      showSuccess('Report submitted successfully. Thank you for your feedback!');
    } catch (err) {
      console.error('Error submitting report:', err);
      showError('Failed to submit report. Please try again.');
    } finally {
      setSubmittingReport(false);
      setReportDropdownOpen(false);
    }
  };

  const loadCollection = async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get<any>(`/collections/${id}`);

      if (response.success && response.data) {
        setCollection(response.data);
      } else {
        setError('Collection not found');
      }
    } catch (err) {
      console.error('Error loading collection:', err);
      if (err instanceof Error && err.message.includes('404')) {
        setError('Collection not found');
      } else {
        setError('Failed to load collection details');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBuyNow = (nft: NFT, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (!isAuthenticated || !user?.xrpl_address || !nft.min_sell_price) return;

    // Get broker address and offer ID from the NFT's lowest active offer
    const lowestOffer = nft.active_offers?.[0];
    const brokerAddress = lowestOffer?.destination || null;
    setCurrentBrokerAddress(brokerAddress);
    setCurrentOfferIndex(lowestOffer?.offer_id || null);

    // min_sell_price is in XRP, convert to drops for fee calculation
    const priceInDrops = Math.floor(nft.min_sell_price * 1_000_000);
    const amount = priceInDrops / 1_000_000;
    const platformFee = calculateMarketplaceFee(priceInDrops, brokerAddress) / 1_000_000;
    const totalCost = amount + platformFee;

    setTransactionFees({
      amount,
      platformFee,
      networkFee: 0,
      totalCost,
      isMarketplaceBrokered: !!brokerAddress,
      feePercentage: getMarketplaceFeePercentage(brokerAddress),
      marketplaceName: getMarketplaceName(brokerAddress),
    });

    setTransactionNFTDetails({
      name: nft.name || 'Unknown NFT',
      imageUrl: nft.thumbnail_url || nft.media_url || nft.image_url || '',
      collectionName: collection?.name || 'Unknown Collection',
      tokenId: nft.nft_token_id
    });

    setTransactionStage('confirmation');
    setTxHash(undefined);
    setShowTransactionModal(true);
  };

  const handleAddToCart = (nft: NFT, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (!nft.min_sell_price || !nft.active_offers?.length) return;

    const lowestOffer = nft.active_offers[0];
    const priceXrp = Number(nft.min_sell_price);
    const priceInDrops = Math.floor(priceXrp * 1_000_000);

    const cartItem: CartItem = {
      nft_token_id: nft.nft_token_id,
      name: nft.name,
      thumbnail_url: nft.thumbnail_url || null,
      image_url: nft.image_url || null,
      collection_name: collection?.name || null,
      collection_slug: null,
      owner_address: nft.owner_address,
      offer_id: lowestOffer.offer_id,
      price_drops: priceInDrops,
      price_xrp: priceXrp,
      destination: lowestOffer.destination || null,
      expiration: lowestOffer.expiration || null,
      added_at: Date.now(),
      rarity_rank: nft.rarity_rank || null,
    };

    const added = cartStore.addItem(cartItem);
    if (added) {
      showSuccess('Added to cart');
    } else if (cartStore.isInCart(nft.nft_token_id)) {
      showError('Already in cart');
    } else if (cartStore.items.length >= 20) {
      showError('Cart is full (max 20 items)');
    } else {
      showError('Cannot add your own NFT to cart');
    }
  };

  const handleConfirmTransaction = async () => {
    if (!transactionNFTDetails || !user?.xrpl_address || !transactionFees) return;

    setProcessing(true);

    try {
      const brokerAddress = currentBrokerAddress;

      if (brokerAddress) {
        // Marketplace-brokered: create matching buy offer
        const nftResponse = await apiClient.get<any>(`/nfts/${transactionNFTDetails.tokenId}`);
        if (!nftResponse.success || !nftResponse.data?.nft) {
          throw new Error('NFT not found');
        }

        const nftOwner = nftResponse.data.nft.owner_address;
        const sellPriceDrops = transactionFees.amount * 1_000_000;
        const buyOfferAmount = calculateBuyAmount(sellPriceDrops, brokerAddress);

        const response = await apiClient.post<any>('/offers/create', {
          nftTokenId: transactionNFTDetails.tokenId,
          offerType: 'buy',
          amount: buyOfferAmount,
          owner: nftOwner,
          userAddress: user.xrpl_address,
          destination: brokerAddress,
        });

        if (response.success && (response.data?.signingIntent || response.data?.payload)) {
          setXamanPayload(toSigningPayload(response.data.signingIntent ?? response.data.payload));
          setTransactionStage('signing');
        } else {
          setTransactionStage('error');
        }
      } else {
        // P2P: directly accept the sell offer
        if (!currentOfferIndex) {
          setTransactionStage('error');
          return;
        }

        const response = await apiClient.post<any>('/offers/accept-p2p', {
          nftTokenId: transactionNFTDetails.tokenId,
          offerIndex: currentOfferIndex,
          userAddress: user.xrpl_address,
          offerType: 'sell',
        });

        if (response.success && (response.data?.signingIntent || response.data?.payload)) {
          setXamanPayload(toSigningPayload(response.data.signingIntent ?? response.data.payload));
          setTransactionStage('signing');
        } else {
          setTransactionStage('error');
        }
      }
    } catch (err) {
      setTransactionStage('error');
    } finally {
      setProcessing(false);
    }
  };

  const handleTransactionSuccess = (hash: string) => {
    const purchasedTokenId = transactionNFTDetails?.tokenId;

    setTxHash(hash);
    showSuccess('Purchase successful! The NFT will be transferred to your wallet shortly.');

    // Optimistic: brokered settlement can lag signing, so drop the Buy Now button immediately.
    if (purchasedTokenId) {
      setNfts(prev => prev.map(n =>
        n.nft_token_id === purchasedTokenId
          ? { ...n, min_sell_price: null as any, active_offers: [] }
          : n
      ));
    }

    // TransactionConfirmation has no 'success' stage — leaving the modal open strands the user.
    setShowTransactionModal(false);
    setTransactionStage('confirmation');
    setXamanPayload(null);
    setTransactionFees(null);
    setTransactionNFTDetails(null);
    setTxHash(undefined);
    setCurrentBrokerAddress(null);
    setCurrentOfferIndex(null);
  };

  const handleTransactionError = (errorMsg: string) => {
    showError(errorMsg);
    setTransactionStage('error');
  };

  const handleCloseTransactionModal = () => {
    setShowTransactionModal(false);
    setTransactionStage('confirmation');
    setXamanPayload(null);
    setTransactionFees(null);
    setTransactionNFTDetails(null);
    setTxHash(undefined);
  };

  const loadNFTs = async () => {
    if (!id) return;

    setNftsLoading(true);

    try {
      const params = filters.buildApiParams();
      params.set('page', currentPage.toString());
      params.set('limit', '20');

      // Add ownership filter (only when logged in)
      if (ownershipFilter !== 'all' && user?.xrpl_address) {
        if (ownershipFilter === 'owned') {
          params.set('owner', user.xrpl_address);
        } else if (ownershipFilter === 'not-owned') {
          params.set('excludeOwner', user.xrpl_address);
        }
      }

      const response = await apiClient.get<any>(`/collections/${id}/nfts?${params}`);

      if (response.success && response.data) {
        if (currentPage === 1) {
          setNfts(response.data.nfts || []);
        } else {
          setNfts(prev => [...prev, ...(response.data.nfts || [])]);
        }
        if (response.data.pagination) {
          setTotalPages(response.data.pagination.pages || 1);
        }
      }
    } catch (err) {
      console.error('Error loading NFTs:', err);
      setError('Failed to load NFTs');
    } finally {
      setNftsLoading(false);
    }
  };

  const handleDownloadFilteredCsv = async () => {
    if (!id || csvDownloading) return;

    setCsvDownloading(true);
    try {
      const params = filters.buildApiParams();
      if (ownershipFilter !== 'all' && user?.xrpl_address) {
        if (ownershipFilter === 'owned') {
          params.set('owner', user.xrpl_address);
        } else if (ownershipFilter === 'not-owned') {
          params.set('excludeOwner', user.xrpl_address);
        }
      }
      // Pagination params don't apply to the export endpoint.
      params.delete('page');
      params.delete('limit');

      const response = await fetch(
        `${API_BASE_URL}/collections/${id}/nfts/export.csv?${params}`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        throw new Error(`Export failed: HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = /filename="?([^"]+)"?/i.exec(disposition);
      const filename = match?.[1] || 'collection-nfts.csv';

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV export failed:', err);
      showError('Failed to export NFTs to CSV');
    } finally {
      setCsvDownloading(false);
    }
  };

  const loadTransactions = async () => {
    if (!id) return;

    setTransactionsLoading(true);

    try {
      const params = new URLSearchParams({
        page: transactionPage.toString(),
        limit: '20',
        type: transactionTypeFilter,
      });

      // Add trait filters
      if (Object.keys(activitySelectedTraits).length > 0) {
        params.append('traits', JSON.stringify(activitySelectedTraits));
      }

      const response = await apiClient.get<any>(`/collections/${id}/transactions?${params}`);

      if (response.success && response.data) {
        if (transactionPage === 1) {
          setTransactions(response.data.transactions || []);
        } else {
          setTransactions(prev => [...prev, ...(response.data.transactions || [])]);
        }
        if (response.data.pagination) {
          setTransactionTotalPages(response.data.pagination.pages || 1);
        }
      }
    } catch (err) {
      console.error('Error loading transactions:', err);
    } finally {
      setTransactionsLoading(false);
    }
  };

  const loadTraitThumbnails = async () => {
    if (!collection?.id) return;

    setTraitThumbnailsLoading(true);

    try {
      const response = await apiClient.get<any>(`/collections/${collection.id}/trait-thumbnails`);

      if (response.success && response.data) {
        setTraitThumbnails(response.data);
      }
    } catch (err) {
      console.error('Error loading trait thumbnails:', err);
    } finally {
      setTraitThumbnailsLoading(false);
    }
  };

  const loadMoreNFTs = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Infinite scroll setup
  const sentinelRef = useInfiniteScroll({
    onLoadMore: loadMoreNFTs,
    enabled: currentPage < totalPages && !nftsLoading && nfts.length > 0,
  });

  const getNFTImageUrl = (nft: NFT): string => {
    return nft.thumbnail_url || nft.media_url || nft.image_url || '';
  };

  const getTraitStats = (): { traitType: string; values: { value: string; count: number; percentage: number }[] }[] => {
    if (!collection?.trait_stats) return [];

    return Object.entries(collection.trait_stats).map(([traitType, values]) => ({
      traitType,
      values: Object.entries(values)
        .map(([value, stats]) => ({
          value,
          count: stats.count,
          percentage: stats.percentage,
        }))
        // Sort by rarity (lowest percentage = rarest first)
        .sort((a, b) => a.percentage - b.percentage),
    }));
  };

  const formatAddress = (address: string): string => {
    return `${address.slice(0, 8)}...${address.slice(-6)}`;
  };

  const getWalletUrl = (address: string): string => {
    return `/wallets/${address}`;
  };

  const getTransactionImageUrl = (tx: Transaction): string => {
    return tx.nft.thumbnail_url || tx.nft.image_url || '';
  };

  const handleTabChange = (newTab: 'nfts' | 'activity' | 'richlist' | 'alerts' | 'offers' | 'bundles' | 'traits' | 'chat' | 'compose') => {
    setActiveTab(newTab);

    // Auto-collapse header when switching to chat tab for more space
    // Auto-expand header when switching away from chat tab
    if (newTab === 'chat') {
      setIsHeaderCollapsed(true);
    } else {
      setIsHeaderCollapsed(false);
    }

    // Mark chat as read immediately when switching to chat tab
    if (newTab === 'chat' && collection?.id && isAuthenticated) {
      markChatAsReadMutation.mutate(collection.id);
    }

    // Update URL with new tab
    const params = new URLSearchParams(searchParams);
    if (newTab === 'activity') {
      params.set('tab', 'activity');
    } else if (newTab === 'richlist') {
      params.set('tab', 'richlist');
    } else if (newTab === 'alerts') {
      params.set('tab', 'alerts');
    } else if (newTab === 'offers') {
      params.set('tab', 'offers');
    } else if (newTab === 'bundles') {
      params.set('tab', 'bundles');
    } else if (newTab === 'traits') {
      params.set('tab', 'traits');
    } else if (newTab === 'chat') {
      params.set('tab', 'chat');
    } else if (newTab === 'compose') {
      params.set('tab', 'compose');
    } else {
      params.delete('tab'); // Default to NFTs, so no param needed
    }
    setSearchParams(params);
  };

  const handleTransactionTypeChange = (newType: TransactionTypeFilter) => {
    setTransactionTypeFilter(newType);
    setTransactionPage(1); // Reset to page 1 when filter changes
    setShowTypeDropdown(false);

    // Update URL with new filter type
    const params = new URLSearchParams(searchParams);
    if (newType === 'sales') {
      params.delete('type'); // Default to sales, so no param needed
    } else {
      params.set('type', newType);
    }
    setSearchParams(params);
  };

  const handleActivityTraitToggle = (traitType: string, value: string) => {
    setActivitySelectedTraits(prev => {
      const current = prev[traitType] || [];
      const isSelected = current.includes(value);

      let newTraits;
      if (isSelected) {
        const updated = current.filter(v => v !== value);
        if (updated.length === 0) {
          const { [traitType]: _, ...rest } = prev;
          newTraits = rest;
        } else {
          newTraits = { ...prev, [traitType]: updated };
        }
      } else {
        newTraits = { ...prev, [traitType]: [...current, value] };
      }

      // Update URL with new traits
      const params = new URLSearchParams(searchParams);
      if (Object.keys(newTraits).length > 0) {
        params.set('activityTraits', JSON.stringify(newTraits));
      } else {
        params.delete('activityTraits');
      }
      setSearchParams(params);

      return newTraits;
    });
  };

  const handleActivityFilterApply = () => {
    setTransactionPage(1); // Reset to page 1 when filters change
    setShowActivityFilters(false);
  };

  const handleActivityFilterClear = () => {
    setActivitySelectedTraits({});
    setTransactionPage(1);

    // Clear traits from URL
    const params = new URLSearchParams(searchParams);
    params.delete('activityTraits');
    setSearchParams(params);
  };

  const loadMoreTransactions = () => {
    if (transactionPage < transactionTotalPages) {
      setTransactionPage(transactionPage + 1);
    }
  };

  // Infinite scroll for transactions
  const transactionSentinelRef = useInfiniteScroll({
    onLoadMore: loadMoreTransactions,
    enabled: transactionPage < transactionTotalPages && !transactionsLoading && transactions.length > 0,
  });

  if (loading) {
    return (
      <DefaultPageLayout>
        <div className="flex h-full w-full items-center justify-center bg-default-background">
          <div className="flex flex-col items-center gap-4">
            <LoadingSpinner size="md" />
            <span className="text-body font-body text-subtext-color">Loading collection...</span>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  if (error && !collection) {
    return (
      <DefaultPageLayout>
        <div className="flex h-full w-full items-center justify-center bg-default-background">
          <div className="flex flex-col items-center gap-4 rounded-md border border-solid border-neutral-border bg-default-background p-8 shadow-sm">
            <span className="text-heading-2 font-heading-2 text-error">Error</span>
            <span className="text-body font-body text-subtext-color">{error}</span>
            <Link to="/collections">
              <Button variant="brand-secondary">Back to Collections</Button>
            </Link>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  if (!collection) return null;

  const traitStats = getTraitStats();

  return (
    <DefaultPageLayout>
      <div className="container max-w-none flex h-full w-full flex-col items-start gap-6 bg-default-background py-6 overflow-auto mobile:gap-4 mobile:py-4">
        {/* Collection Header - Collapsible */}
        <div className="w-full relative">
          {isHeaderCollapsed ? (
            /* Collapsed Header - Just collection name */
            <div className="flex w-full items-center justify-between py-2">
              <div className="flex items-center gap-3">
                {collection.image_url && !profileImageFailed ? (
                  <img
                    className="w-10 h-10 rounded-md object-cover border border-solid border-neutral-border"
                    src={collection.image_url}
                    alt={`${collection.name} profile`}
                    onError={() => setProfileImageFailed(true)}
                  />
                ) : (
                  <div className="w-10 h-10 rounded-md bg-neutral-100 flex items-center justify-center border border-solid border-neutral-border">
                    <span className="text-lg font-bold text-neutral-400">
                      {collection.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <span className="text-heading-2 font-heading-2 text-default-font">
                  {collection.name}
                </span>
                {collection.is_verified && (
                  <FeatherVerified className="text-xl text-brand-700" />
                )}
              </div>
              <button
                onClick={() => setIsHeaderCollapsed(false)}
                className="flex items-center gap-1 text-subtext-color hover:text-default-font transition-colors px-3 py-1.5 rounded-md hover:bg-neutral-100"
              >
                <span className="text-caption">Show details</span>
                <FeatherChevronDown className="w-4 h-4" />
              </button>
            </div>
          ) : (
            /* Expanded Header - Full details */
            <div className="flex w-full items-start gap-8 mobile:flex-col mobile:gap-4">
              {/* Large Profile Image on Left */}
              <div className="flex-shrink-0 mobile:w-full mobile:flex mobile:justify-center">
                {collection.image_url && !profileImageFailed ? (
                  <img
                    className="w-64 h-64 rounded-md object-cover shadow-lg border border-solid border-neutral-border mobile:w-32 mobile:h-32"
                    src={collection.image_url}
                    alt={`${collection.name} profile`}
                    onError={() => setProfileImageFailed(true)}
                  />
                ) : (
                  <div className="w-64 h-64 rounded-md bg-neutral-100 flex items-center justify-center shadow-lg border border-solid border-neutral-border mobile:w-32 mobile:h-32">
                    <span className="text-6xl font-bold text-neutral-400 mobile:text-3xl">
                      {collection.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>

              {/* Collection Info on Right */}
              <div className="flex flex-1 flex-col items-start gap-6 mobile:gap-4 mobile:items-center mobile:text-center">
                {/* Project Link */}
                {collection.project_slug && collection.project_name && (
                  <div className="flex items-center gap-2">
                    <span className="text-heading-3 font-heading-3 text-subtext-color">Project:</span>
                    <Link
                      to={`/projects/${collection.project_slug}`}
                      className="flex items-center gap-2 text-heading-3 font-heading-3 text-brand-700 hover:text-brand-600 underline"
                    >
                      <Avatar
                        size="small"
                        image={collection.project_image_url || undefined}
                        square={true}
                      >
                        {collection.project_name.slice(0, 1)}
                      </Avatar>
                      {collection.project_name}
                    </Link>
                  </div>
                )}
                <div className="flex w-full items-center gap-2 mobile:flex-col mobile:gap-1">
                  <span className="text-5xl font-bold text-default-font mobile:text-2xl">
                    {collection.name}
                  </span>
                  {collection.is_verified && (
                    <FeatherVerified className="text-4xl text-brand-700 mobile:text-2xl" />
                  )}
                  <div className="flex items-center gap-3 ml-4 mobile:ml-0 mobile:mt-2">
                    {collection.social_links?.twitter && (
                      <a href={collection.social_links.twitter} target="_blank" rel="noopener noreferrer">
                        <Button
                          variant="neutral-tertiary"
                          size="small"
                          icon={<FeatherTwitter />}
                          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {}}
                        />
                      </a>
                    )}
                    {(collection.external_url || collection.social_links?.website) && (
                      <a href={collection.external_url || collection.social_links!.website!} target="_blank" rel="noopener noreferrer">
                        <Button
                          variant="neutral-tertiary"
                          size="small"
                          icon={<FeatherGlobe />}
                          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {}}
                        />
                      </a>
                    )}
                    {/* Share Link Button */}
                    <IconButton
                      icon={<FeatherShare />}
                      onClick={() => {
                        const shareUrl = buildShareableUrl(window.location.href, user?.referral_code);
                        navigator.clipboard.writeText(shareUrl);
                        showSuccess('Link copied to clipboard!');
                      }}
                    />
                    {/* Report Issue Dropdown */}
                    <SubframeCore.DropdownMenu.Root open={reportDropdownOpen} onOpenChange={setReportDropdownOpen}>
                      <SubframeCore.DropdownMenu.Trigger asChild>
                        <IconButton
                          icon={<FeatherFlag />}
                          disabled={submittingReport}
                        />
                      </SubframeCore.DropdownMenu.Trigger>
                      <SubframeCore.DropdownMenu.Portal>
                        <SubframeCore.DropdownMenu.Content
                          side="bottom"
                          align="start"
                          sideOffset={4}
                          className="z-50"
                        >
                          <DropdownMenu>
                            <DropdownMenu.DropdownItem
                              icon={<FeatherDollarSign />}
                              onSelect={() => handleReport('incorrect_floor')}
                            >
                              Report incorrect floor
                            </DropdownMenu.DropdownItem>
                            <DropdownMenu.DropdownItem
                              icon={<FeatherImage />}
                              onSelect={() => handleReport('missing_nfts')}
                            >
                              Report missing NFTs
                            </DropdownMenu.DropdownItem>
                            <DropdownMenu.DropdownItem
                              icon={<FeatherActivity />}
                              onSelect={() => handleReport('missing_sales_activity')}
                            >
                              Report missing sales activity
                            </DropdownMenu.DropdownItem>
                            <DropdownMenu.DropdownItem
                              icon={<FeatherAward />}
                              onSelect={() => handleReport('incorrect_rarity_rankings')}
                            >
                              Report incorrect rarity rankings
                            </DropdownMenu.DropdownItem>
                          </DropdownMenu>
                        </SubframeCore.DropdownMenu.Content>
                      </SubframeCore.DropdownMenu.Portal>
                    </SubframeCore.DropdownMenu.Root>
                  </div>
                </div>

                <span className="w-full text-heading-3 font-heading-3 text-default-font hidden sm:block">
                  {collection.description || 'No description available'}
                  {collection.has_points_system && pointsDistribution && pointsDistribution.distribution.length > 1 && (
                    <>
                      {' '}
                      <button
                        type="button"
                        onClick={() => setShowPointsDistribution(true)}
                        className="text-body font-body text-brand-600 hover:underline"
                      >
                        View Points Distribution
                      </button>
                    </>
                  )}
                </span>

                {/* Statistics */}
                <div className="flex items-start gap-12 flex-wrap mobile:gap-4 mobile:justify-center">
                  <div className="flex flex-col items-start mobile:items-center">
                    <span className="text-body font-body text-subtext-color mobile:text-caption mobile:font-caption">
                      Floor Price
                    </span>
                    <span className="text-heading-3 font-heading-3 text-default-font mobile:text-body-bold mobile:font-body-bold">
                      {formatFloorPrice(collection.statistics?.floorPrice || collection.floor_price)}
                    </span>
                  </div>
                  <div className="flex flex-col items-start mobile:items-center">
                    <span className="text-body font-body text-subtext-color mobile:text-caption mobile:font-caption">
                      Items
                    </span>
                    <span className="text-heading-3 font-heading-3 text-default-font mobile:text-body-bold mobile:font-body-bold">
                      {(collection.statistics?.totalSupply || collection.total_supply || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex flex-col items-start mobile:items-center">
                    <span className="text-body font-body text-subtext-color mobile:text-caption mobile:font-caption">
                      Total Volume
                    </span>
                    <span className="text-heading-3 font-heading-3 text-default-font mobile:text-body-bold mobile:font-body-bold">
                      {formatVolume(collection.statistics?.volumeTotal || collection.volume_all_time)}
                    </span>
                  </div>
                  <div className="flex flex-col items-start mobile:items-center">
                    <span className="text-body font-body text-subtext-color mobile:text-caption mobile:font-caption">
                      Holders
                    </span>
                    <span className="text-heading-3 font-heading-3 text-default-font mobile:text-body-bold mobile:font-body-bold">
                      {(collection.statistics?.owners || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex flex-col items-start mobile:items-center">
                    <span className="text-body font-body text-subtext-color mobile:text-caption mobile:font-caption">
                      Listed
                    </span>
                    <span className="text-heading-3 font-heading-3 text-default-font mobile:text-body-bold mobile:font-body-bold">
                      {(() => {
                        const totalSupply = collection.statistics?.totalSupply || collection.total_supply || 0;
                        const listedItems = collection.statistics?.listedItems || 0;
                        if (totalSupply === 0) return '0%';
                        return `${((listedItems / totalSupply) * 100).toFixed(1)}%`;
                      })()}
                    </span>
                  </div>
                  {/* XRPL VoteBoard Score - inline with other stats */}
                  {collection.xrpl_voteboard_score !== null && collection.xrpl_voteboard_score !== undefined && (
                    <div className="flex flex-col items-start mobile:items-center">
                      <span className="text-body font-body text-subtext-color mobile:text-caption mobile:font-caption">
                        Vote Board Rank
                      </span>
                      <div className="flex items-center gap-1.5">
                        {collection.xrpl_voteboard_data?.trustTier && (
                          <VoteBoardTierBadge
                            tier={collection.xrpl_voteboard_data.trustTier.tier}
                            label={collection.xrpl_voteboard_data.trustTier.label}
                            color={collection.xrpl_voteboard_data.trustTier.color}
                          />
                        )}
                        {collection.xrpl_voteboard_data?.currentPosition != null && (
                          <span className="text-heading-3 font-heading-3 text-default-font mobile:text-body-bold mobile:font-body-bold">
                            #{collection.xrpl_voteboard_data.currentPosition}
                            {collection.xrpl_voteboard_data.movement?.direction === 'up' && (
                              <span className="ml-0.5 text-success-700">
                                {'\u2191'}{collection.xrpl_voteboard_data.movement.positions}
                              </span>
                            )}
                            {collection.xrpl_voteboard_data.movement?.direction === 'down' && (
                              <span className="ml-0.5 text-error-700">
                                {'\u2193'}{collection.xrpl_voteboard_data.movement.positions}
                              </span>
                            )}
                          </span>
                        )}
                        <span className="text-body font-body text-subtext-color">
                          ({Math.round(collection.xrpl_voteboard_score)}pts)
                        </span>
                        <a
                          href={getVoteBoardUrl(collection.xrpl_voteboard_data?.slug)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-0.5 text-caption font-caption text-brand-600 hover:text-brand-500 transition-colors"
                        >
                          <FeatherCheckSquare className="w-3 h-3" />
                          Vote
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Buttons - hidden during active minting */}
                {!isMintingLive && (
                  <div className="flex gap-3 flex-wrap mobile:flex-col mobile:w-full">
                    {collection.mint_url && (
                      <Button
                        variant="brand-primary"
                        icon={<FeatherExternalLink />}
                        onClick={() => window.open(collection.mint_url!, '_blank', 'noopener,noreferrer')}
                        className="mobile:w-full !bg-green-800 hover:!bg-green-900 !border-green-800 hover:!border-green-900 [&_span]:!text-white [&_span]:!font-bold [&_svg]:!stroke-white"
                      >
                        Mint
                      </Button>
                    )}
                    <Button
                      variant="neutral-secondary"
                      icon={<FeatherBarChart2 />}
                      onClick={() => setShowFloorPriceModal(true)}
                      className="mobile:w-full"
                    >
                      Collection Analytics
                    </Button>
                    {isAuthenticated && (
                      <CreateCollectionOfferPopover
                        collectionId={collection.id}
                        collectionName={collection.name}
                        open={showCollectionOfferModal}
                        onOpenChange={setShowCollectionOfferModal}
                        onSuccess={() => setCollectionOffersRefreshKey(k => k + 1)}
                        availableTraits={
                          collection.trait_stats
                            ? Object.entries(collection.trait_stats).reduce((acc, [traitType, values]) => {
                                acc[traitType] = Object.keys(values);
                                return acc;
                              }, {} as { [traitType: string]: string[] })
                            : {}
                        }
                        traitStats={collection.trait_stats}
                      >
                        <Button
                          variant="brand-primary"
                          className="mobile:w-full"
                        >
                          Make Collection Offer
                        </Button>
                      </CreateCollectionOfferPopover>
                    )}
                  </div>
                )}

                {/* Collapse Button - mobile only, below buttons */}
                <button
                  onClick={() => setIsHeaderCollapsed(true)}
                  className="hidden mobile:flex items-center justify-center gap-1 text-subtext-color hover:text-default-font transition-colors py-1"
                >
                  <span className="text-caption">Hide details</span>
                  <FeatherChevronDown className="w-3 h-3 rotate-180" />
                </button>
              </div>

              {/* Collapse Button - Desktop only, absolute positioned */}
              <button
                onClick={() => setIsHeaderCollapsed(true)}
                className="absolute bottom-0 right-0 mobile:hidden flex items-center gap-1 text-subtext-color hover:text-default-font transition-colors px-3 py-1.5 rounded-md hover:bg-neutral-100"
              >
                <span className="text-caption">Hide details</span>
                <FeatherChevronDown className="w-4 h-4 rotate-180" />
              </button>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

        {/* Ambassador Mint Banner - shown when ambassador has allocation for this launch */}
        {hasActiveLaunch && ambassadorAllocation && collection.activeLaunch?.mint_phase === 'public' && (
          <AmbassadorMintBanner
            allocationId={ambassadorAllocation.id}
            claimed={ambassadorAllocation.status === 'claimed'}
            xPostUrl={ambassadorAllocation.x_post_url}
            xPostDeadlineAt={ambassadorAllocation.x_post_deadline_at}
            lockExpiresAt={ambassadorAllocation.lock_expires_at}
            expiresAt={ambassadorAllocation.expires_at}
            onClaim={handleAmbassadorClaim}
            onSubmitXPost={handleSubmitAmbassadorXPost}
            claimLoading={ambassadorClaimLoading}
          />
        )}

        {/* Mint Section - shown when collection has an active launch */}
        {hasActiveLaunch && collection.activeLaunch && (
          <MintSection
            launch={collection.activeLaunch as ActiveLaunchForMint}
            isAuthenticated={isAuthenticated}
            quantity={mint.quantity}
            onQuantityChange={mint.setQuantity}
            mintLoading={mint.mintLoading}
            mintError={mint.mintError}
            onMint={mint.handleMint}
            pendingMint={mint.pendingMint}
            pendingMintCount={mint.pendingMintCount}
            resumeLoading={mint.resumeLoading}
            onResume={mint.handleResume}
            onOpenCancelModal={mint.openCancelModal}
            showPaymentModal={mint.showPaymentModal}
            paymentPayload={mint.paymentPayload}
            onClosePaymentModal={mint.closePaymentModal}
            onPaymentSuccess={mint.handlePaymentSuccess}
            onPaymentRejected={mint.handlePaymentRejected}
            onPaymentExpired={mint.handlePaymentExpired}
            mintStatus={mint.mintStatus}
            showMintStatus={mint.showMintStatus}
            onCloseMintStatus={mint.closeMintStatusModal}
            currentMintCount={mint.currentMintIds.length}
            showCancelModal={mint.showCancelModal}
            cancelCountdown={mint.cancelCountdown}
            cancelLoading={mint.cancelLoading}
            onCloseCancelModal={mint.closeCancelModal}
            onCancel={mint.handleCancel}
            editions={isEditionLaunch ? dropEditions : null}
            selectedEditionId={mint.selectedEditionId}
            onSelectEdition={mint.setSelectedEditionId}
            compact={true}
            referral={
              collection.activeLaunch?.mint_phase === 'public' &&
              collection.activeLaunch?.referral_enabled &&
              isAuthenticated &&
              user?.referral_code &&
              user?.xrpl_address !== collection.creator_address
                ? {
                    referralCode: user.referral_code,
                    launchSlug,
                    referralBps: collection.activeLaunch.referral_bps ?? 0,
                    mintPriceDrops: collection.activeLaunch.mint_price_drops ?? 0,
                    serviceFeePercent: collection.activeLaunch.service_fee_percent ?? 5,
                  }
                : undefined
            }
          />
        )}

        {/* Mutation Countdown Banner */}
        {mutationCountdown && mutationCountdown.scheduledAt && (
          <MutationCountdownBanner
            message={mutationCountdown.message}
            scheduledAt={mutationCountdown.scheduledAt}
            totalCount={mutationCountdown.totalCount}
          />
        )}

        {/* Sweepstakes Contest Banner */}
        {sweepstakesContest && collection && (
          ['active', 'scheduled', 'snapshot_pending'].includes(sweepstakesContest.contest.status)
            ? <ContestBanner contestView={sweepstakesContest} collectionSlug={id || ''} />
            : ['ended', 'resolved'].includes(sweepstakesContest.contest.status)
              ? <ResultsBanner contestView={sweepstakesContest} />
              : null
        )}

        {/* Mint Rewards Banner (from launch) */}
        {mintRewardsContest && collection && !sweepstakesContest && (
          ['active', 'scheduled'].includes(mintRewardsContest.contest.status)
            ? <ContestBanner contestView={mintRewardsContest} collectionSlug={id || ''} />
            : ['ended', 'resolved'].includes(mintRewardsContest.contest.status)
              ? <ResultsBanner contestView={mintRewardsContest} />
              : null
        )}

        {/* Tabs Section */}
        <div className="flex w-full flex-col items-start gap-6">
          {/* Scrollable tabs container for mobile */}
          <div className="relative w-full">
            <div
              ref={tabsContainerRef}
              onScroll={checkTabsOverflow}
              className="w-full overflow-x-auto scrollbar-hide"
            >
              <Tabs className="min-w-max">
                <Tabs.Item
                  active={activeTab === 'nfts'}
                  onClick={() => handleTabChange('nfts')}
                >
                  NFTs
                </Tabs.Item>
                <Tabs.Item
                  active={activeTab === 'activity'}
                  onClick={() => handleTabChange('activity')}
                >
                  Activity
                </Tabs.Item>
                <Tabs.Item
                  active={activeTab === 'richlist'}
                  onClick={() => handleTabChange('richlist')}
                >
                  Richlist
                </Tabs.Item>
                <Tabs.Item
                  active={activeTab === 'alerts'}
                  onClick={() => handleTabChange('alerts')}
                >
                  <div className="flex items-center gap-2">
                    <FeatherBell className="hidden sm:block h-4 w-4" />
                    Alerts
                  </div>
                </Tabs.Item>
                <Tabs.Item
                  active={activeTab === 'chat'}
                  onClick={() => handleTabChange('chat')}
                >
                  <div className="flex items-center gap-2">
                    <span>Chat</span>
                    {activeTab !== 'chat' && chatUnreadCount > 0 && (
                      <Badge variant="brand">
                        {chatUnreadCount > 99 ? '99+' : chatUnreadCount}
                      </Badge>
                    )}
                  </div>
                </Tabs.Item>
                <Tabs.Item
                  active={activeTab === 'offers'}
                  onClick={() => handleTabChange('offers')}
                >
                  <div className="flex items-center gap-2">
                    <span className="hidden sm:inline">Collection Offers</span>
                    <span className="sm:hidden">Offers</span>
                    {collectionOfferStats && collectionOfferStats.activeOfferCount > 0 && (
                      <Badge variant="brand" className="hidden sm:inline-flex">
                        {collectionOfferStats.bestOfferXrp
                          ? `${formatPrice(collectionOfferStats.bestOfferXrp)}`
                          : collectionOfferStats.activeOfferCount}
                      </Badge>
                    )}
                  </div>
                </Tabs.Item>
                <Tabs.Item
                  active={activeTab === 'bundles'}
                  onClick={() => handleTabChange('bundles')}
                >
                  Bundles
                </Tabs.Item>
                {!collection.skip_trait_stats && (
                  <Tabs.Item
                    active={activeTab === 'traits'}
                    onClick={() => handleTabChange('traits')}
                  >
                    Traits
                  </Tabs.Item>
                )}
                {featureFlags.composable && collection.activeLaunch?.collection_type === 'composable' && collection.activeLaunch?.compose_enabled && (
                  <Tabs.Item
                    active={activeTab === 'compose'}
                    onClick={() => handleTabChange('compose')}
                  >
                    <div className="flex items-center gap-2">
                      <FeatherZap className="h-4 w-4" />
                      <span>Build</span>
                    </div>
                  </Tabs.Item>
                )}
              </Tabs>
            </div>
            {/* Scroll indicator - shows when there's more content to scroll */}
            {canScrollTabs && (
              <div className="absolute right-0 top-1/2 -translate-y-1/2 sm:hidden pointer-events-none">
                <div className="flex items-center gap-0.5 text-subtext-color animate-pulse">
                  <FeatherChevronRight className="w-4 h-4" />
                </div>
              </div>
            )}
          </div>

          {/* NFTs Tab Content */}
          {activeTab === 'nfts' && (
            <div className="flex w-full flex-col items-start gap-6">
              <div className="flex w-full flex-col items-start gap-4">

            <NFTFilterBar
              filters={filters}
              traitStats={traitStats}
              hasVariablePoints={hasVariablePoints}
              isAuthenticated={isAuthenticated}
              hideOwnershipFilter={false}
              rightContent={
                isAuthenticated && sweepableNfts.length > 0 ? (
                  <IconButton
                    icon={<BroomIcon />}
                    onClick={isSweepMode ? exitSweepMode : enterSweepMode}
                    variant={isSweepMode ? 'brand-primary' : 'neutral-secondary'}
                    title="Sweep the floor"
                  />
                ) : undefined
              }
              activeFiltersRightContent={
                <Button
                  variant="neutral-tertiary"
                  size="small"
                  icon={<FeatherDownload />}
                  disabled={csvDownloading}
                  onClick={handleDownloadFilteredCsv}
                  title="Download a CSV of all NFTs matching the active filters"
                >
                  {csvDownloading ? 'Downloading…' : 'Download CSV'}
                </Button>
              }
            />

            {/* Contest Eligible Filter */}
            {activeContestId && (
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={contestEligibleFilter}
                    onChange={(e) => setContestEligibleFilter(e.target.checked)}
                    className="rounded border-neutral-border text-brand-600 focus:ring-brand-600"
                  />
                  <span className="text-default-font">Contest Eligible Only</span>
                </label>
              </div>
            )}

            {/* Sweep Mode Bar */}
            {isSweepMode && (
              <SweepModeBar
                nfts={sweepableNfts as any}
                sweepCount={sweepCount}
                onSweepCountChange={setSweepCount}
                onContinue={handleSweepContinue}
                onCancel={exitSweepMode}
              />
            )}

            {/* NFT Grid */}
            {nftsLoading && currentPage === 1 ? (
              <div className="w-full items-start gap-6 grid grid-cols-4 mobile:grid-cols-2 mobile:gap-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="flex flex-col items-start overflow-hidden rounded-md border border-solid border-neutral-border bg-default-background shadow-sm animate-pulse">
                    <div className="w-full flex-none aspect-square bg-neutral-200" />
                    <div className="flex w-full flex-col items-start gap-3 px-4 py-4">
                      <div className="h-4 w-3/4 bg-neutral-200 rounded" />
                      <div className="h-3 w-1/2 bg-neutral-200 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : nfts.length === 0 ? (
              <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-default-background p-12 mobile:p-6">
                <span className="text-body font-body text-subtext-color">
                  No NFTs found with the current filters
                </span>
              </div>
            ) : (
              <div className="w-full items-start gap-6 grid grid-cols-4 mobile:grid-cols-2 mobile:gap-3">
                {(contestEligibleFilter
                  ? nfts.filter(nft => {
                      const lowestOffer = nft.active_offers?.[0];
                      return lowestOffer && eligibleOfferIds.has(lowestOffer.offer_id);
                    })
                  : nfts
                ).map((nft) => {
                  const lowestOffer = nft.active_offers?.[0];
                  const isContestEligible = activeContestId ? !!(lowestOffer && eligibleOfferIds.has(lowestOffer.offer_id)) : false;
                  return (
                    <NFTCard
                      key={nft.id || nft.nft_token_id}
                      nft={nft}
                      showBuyButton={true}
                      onBuyClick={handleBuyNow}
                      isAuthenticated={isAuthenticated}
                      totalSupply={collection.total_supply}
                      isSelected={isSweepMode && selectedForSweep.some(s => s.id === nft.id)}
                      onAddToCart={featureFlags.shoppingCart && nft.min_sell_price && nft.active_offers?.length ? handleAddToCart : undefined}
                      isInCart={featureFlags.shoppingCart && cartStore.isInCart(nft.nft_token_id)}
                      contestEligible={isContestEligible}
                      useOriginalMedia={collection.use_original_media}
                      showPointsPerXrp={hasVariablePoints}
                    />
                  );
                })}
              </div>
            )}

            {/* Infinite scroll sentinel - triggers load when scrolled into view */}
            {currentPage < totalPages && nfts.length > 0 && (
              <div ref={sentinelRef} className="h-4 w-full" />
            )}

            {/* Loading indicator for infinite scroll */}
            {nftsLoading && currentPage > 1 && (
              <div className="flex w-full items-center justify-center py-8">
                <LoadingSpinner size="md" />
              </div>
            )}
              </div>
            </div>
          )}

          {/* Activity Tab Content */}
          {activeTab === 'activity' && (
            <div className="flex w-full flex-col items-start gap-6">
              {/* Filters and Type Dropdown */}
              <div className="flex w-full items-center gap-2 flex-wrap">
                <span className="text-body font-body text-subtext-color">Type:</span>
                <div className="relative">
                  <Button
                    variant="neutral-secondary"
                    iconRight={<FeatherChevronDown />}
                    onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                  >
                    {transactionTypeFilter === 'sales' ? 'Sales' : transactionTypeFilter === 'transfers' ? 'Transfers' : 'All'}
                  </Button>

                  {showTypeDropdown && (
                    <div className="absolute top-full left-0 mt-2 w-40 rounded-md border border-solid border-neutral-border bg-default-background shadow-lg z-10">
                      {(['sales', 'transfers', 'all'] as TransactionTypeFilter[]).map((type) => (
                        <button
                          key={type}
                          onClick={() => handleTransactionTypeChange(type)}
                          className={`w-full text-left px-4 py-2 text-body font-body hover:bg-neutral-100 transition-colors ${
                            transactionTypeFilter === type ? 'bg-brand-100 text-brand-700' : 'text-default-font'
                          }`}
                        >
                          {type === 'sales' ? 'Sales' : type === 'transfers' ? 'Transfers' : 'All'}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <Button
                  variant="neutral-secondary"
                  icon={<FeatherSlidersHorizontal />}
                  onClick={() => setShowActivityFilters(!showActivityFilters)}
                >
                  Filters
                </Button>
              </div>

              {/* Filters Panel */}
              {showActivityFilters && (
                <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background p-4">
                  <span className="text-body-bold font-body-bold text-default-font">Filters</span>

                  <div className="flex w-full flex-col gap-4">
                    {/* Trait Filters */}
                    {traitStats.length > 0 && (
                      <div className="flex flex-col gap-4">
                        <span className="text-caption font-caption text-subtext-color">
                          Traits
                        </span>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {traitStats.map((trait) => {
                            const isExpanded = expandedTraits.has(trait.traitType);
                            return (
                              <div key={trait.traitType} className="flex flex-col gap-2">
                                {/* Collapsible header */}
                                <button
                                  onClick={() => toggleTraitExpanded(trait.traitType)}
                                  className="flex items-center gap-2 text-left cursor-pointer hover:opacity-70 transition-opacity"
                                >
                                  <FeatherChevronRight
                                    className={`text-caption font-caption text-subtext-color transition-transform ${
                                      isExpanded ? 'rotate-90' : ''
                                    }`}
                                  />
                                  <span className="text-caption-bold font-caption-bold text-default-font">
                                    {trait.traitType}
                                  </span>
                                </button>

                                {/* Collapsible content */}
                                {isExpanded && (
                                  <div className="flex flex-col gap-1 max-h-48 overflow-y-auto">
                                    {trait.values.map((val) => {
                                      const isSelected = activitySelectedTraits[trait.traitType]?.includes(val.value);
                                      const rarityInfo = getRarityInfoInCategory(val.percentage, trait.values.map(v => v.percentage));
                                      const isLegendary = rarityInfo.tier === 'legendary';
                                      return (
                                        <label
                                          key={val.value}
                                          className="flex items-center gap-2 cursor-pointer hover:bg-neutral-50 px-2 py-1 rounded"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => handleActivityTraitToggle(trait.traitType, val.value)}
                                            className="cursor-pointer"
                                          />
                                          <span
                                            className={`text-caption font-caption flex-1 ${isLegendary ? 'legendary-shimmer font-semibold' : ''}`}
                                            style={!isLegendary ? { color: rarityInfo.color } : {}}
                                          >
                                            {val.value}
                                          </span>
                                          <span
                                            className={`text-caption font-caption ${isLegendary ? 'legendary-shimmer' : ''}`}
                                            style={!isLegendary ? { color: rarityInfo.color, opacity: 0.8 } : {}}
                                          >
                                            {val.percentage.toFixed(1)}%
                                          </span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Filter Actions */}
                    <div className="flex gap-2">
                      <Button variant="brand-primary" onClick={handleActivityFilterApply}>
                        Apply Filters
                      </Button>
                      <Button variant="neutral-secondary" onClick={handleActivityFilterClear}>
                        Clear All
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Selected Traits Display */}
              {Object.keys(activitySelectedTraits).length > 0 && (
                <div className="flex w-full items-center gap-2 flex-wrap">
                  <span className="text-caption font-caption text-subtext-color">Active filters:</span>
                  {Object.entries(activitySelectedTraits).map(([traitType, values]) =>
                    values.map((value) => (
                      <Badge
                        key={`${traitType}:${value}`}
                        variant="neutral"
                        iconRight={
                          <button onClick={() => handleActivityTraitToggle(traitType, value)}>
                            <FeatherX className="w-3 h-3" />
                          </button>
                        }
                      >
                        {traitType}: {value}
                      </Badge>
                    ))
                  )}
                </div>
              )}

              {/* Transactions Table */}
              {transactionsLoading && transactionPage === 1 ? (
                <div className="w-full text-center py-8">
                  <LoadingSpinner size="md" />
                </div>
              ) : transactions.length === 0 ? (
                <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-default-background p-12">
                  <span className="text-body font-body text-subtext-color">
                    No transactions found
                  </span>
                </div>
              ) : (
                <>
                  <div className="w-full overflow-x-auto">
                    <Table
                      header={
                        <Table.HeaderRow>
                          <Table.HeaderCell>Item</Table.HeaderCell>
                          <Table.HeaderCell>Event</Table.HeaderCell>
                          <Table.HeaderCell>Price</Table.HeaderCell>
                          <Table.HeaderCell>Time</Table.HeaderCell>
                          <Table.HeaderCell>From</Table.HeaderCell>
                          <Table.HeaderCell>To</Table.HeaderCell>
                        </Table.HeaderRow>
                      }
                    >
                    {transactions.map((tx) => (
                      <Table.Row key={tx.id}>
                        <Table.Cell className="min-w-[180px]">
                          <Link
                            to={`/nfts/${tx.nft.nft_id}`}
                            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                          >
                            {getTransactionImageUrl(tx) && (
                              <img
                                src={getTransactionImageUrl(tx)}
                                alt={tx.nft.name}
                                className="w-10 h-10 rounded object-cover"
                              />
                            )}
                            <span className="text-body font-body text-default-font">
                              {tx.nft.name || `NFT #${tx.nft.nft_id.slice(0, 8)}...`}
                            </span>
                          </Link>
                        </Table.Cell>
                        <Table.Cell>
                          <Badge variant={tx.event === 'Sale' ? 'brand' : 'neutral'}>
                            {tx.event}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell>
                          <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                            {tx.amount && tx.amount > 0 ? `${formatPrice(tx.amount)}` : '−'}
                          </span>
                        </Table.Cell>
                        <Table.Cell>
                          <span className="whitespace-nowrap text-body font-body text-default-font">
                            {formatDistanceToNow(new Date(tx.occurred_at), { addSuffix: true }).replace(/^about /, '')}
                          </span>
                        </Table.Cell>
                        <Table.Cell>
                          {tx.from_address ? (
                            <Link
                              to={getWalletUrl(tx.from_address)}
                              className="whitespace-nowrap text-body font-body text-brand-700 hover:text-brand-600 underline"
                            >
                              {formatAddress(tx.from_address)}
                            </Link>
                          ) : (
                            <span className="whitespace-nowrap text-body font-body text-subtext-color">
                              −
                            </span>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          {tx.to_address ? (
                            <Link
                              to={getWalletUrl(tx.to_address)}
                              className="whitespace-nowrap text-body font-body text-brand-700 hover:text-brand-600 underline"
                            >
                              {formatAddress(tx.to_address)}
                            </Link>
                          ) : (
                            <span className="whitespace-nowrap text-body font-body text-subtext-color">
                              −
                            </span>
                          )}
                        </Table.Cell>
                      </Table.Row>
                      ))}
                    </Table>
                  </div>

                  {/* Infinite scroll sentinel */}
                  {transactionPage < transactionTotalPages && transactions.length > 0 && (
                    <div ref={transactionSentinelRef} className="h-4 w-full" />
                  )}

                  {/* Loading indicator for infinite scroll */}
                  {transactionsLoading && transactionPage > 1 && (
                    <div className="flex w-full items-center justify-center py-8">
                      <LoadingSpinner size="md" />
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Richlist Tab Content */}
          {activeTab === 'richlist' && (
            <div className="flex w-full flex-col items-start gap-4">
              <div className="flex flex-col gap-1 w-full">
                <h3 className="text-heading-3 font-heading-3 text-default-font">
                  Holder Rankings
                </h3>
                <p className="text-body text-subtext-color">
                  {collection?.has_points_system
                    ? 'Holders ranked by total points from NFTs held in this collection.'
                    : 'Holders ranked by number of NFTs held in this collection.'}
                </p>
              </div>

              {richlistLoading ? (
                <div className="flex w-full items-center justify-center py-12">
                  <LoadingSpinner size="md" />
                </div>
              ) : richlist ? (
                <div className="w-full">
                  <RichlistTable richlist={richlist} collectionName={collection?.name} />
                </div>
              ) : (
                <div className="flex w-full items-center justify-center py-12 text-subtext-color">
                  Failed to load richlist data
                </div>
              )}
            </div>
          )}

          {/* Offers Tab Content */}
          {activeTab === 'offers' && collection && (
            <div className="flex w-full flex-col items-start gap-4">
              {/* Explanatory text */}
              <div className="flex flex-col gap-1 w-full">
                <p className="text-body text-subtext-color">
                  Collection offers are open bids to buy <span className="text-default-font font-medium">any NFT</span> in this collection at a set price.
                  If you own an NFT in this collection, you can accept an offer to sell it instantly.
                </p>
                <p className="text-caption text-subtext-color">
                  Each offer can only be accepted once. When accepted, the offer is removed and the NFT is transferred to the buyer.
                </p>
              </div>

              <CollectionOffersList
                collectionId={collection.id}
                userOwnsNfts={userOwnsNftsInCollection}
                onAcceptOffer={handleAcceptOffer}
                refreshKey={collectionOffersRefreshKey}
              />
            </div>
          )}

          {/* Bundles Tab Content */}
          {activeTab === 'bundles' && collection && (
            <BundlesTab
              collectionId={collection.id}
              collectionName={collection.name}
              userNfts={userNftsForAccept.map((nft): SelectableNFT => ({
                nftTokenId: nft.nft_token_id,
                name: nft.name,
                imageUrl: nft.image_url,
                thumbnailUrl: nft.thumbnail_url,
                rarityRank: nft.rarity_rank,
                totalSupply: collection.total_supply,
              }))}
              onRefresh={async () => {
                if (!user?.xrpl_address || !collection?.id) return;
                setLoadingUserNfts(true);
                try {
                  const response = await apiClient.get<any>(
                    `/collections/${collection.id}/nfts?owner=${user.xrpl_address}&limit=100`
                  );
                  setUserNftsForAccept(response.data?.nfts || []);
                } catch (err) {
                  console.error('Failed to fetch user NFTs:', err);
                } finally {
                  setLoadingUserNfts(false);
                }
              }}
            />
          )}

          {/* Alerts Tab Content */}
          {activeTab === 'alerts' && (
            <div className="flex w-full flex-col items-start gap-6">
              {/* Header */}
              <div className="flex w-full flex-col items-start gap-2">
                <div className="flex items-center gap-2">
                  <FeatherBell className="h-6 w-6 text-brand-600" />
                  <span className="text-heading-2 font-heading-2 text-default-font">
                    Collection Alerts
                  </span>
                </div>
                <span className="text-body font-body text-subtext-color">
                  Get notified when specific events occur in this collection. Set price ranges and trait filters to customize your alerts.
                </span>
              </div>

              {isAuthenticated ? (
                <>
                  {/* Create Alert Buttons */}
                  <div className="flex w-full flex-col items-start gap-3">
                    <span className="text-label font-label text-default-font">
                      Create New Alert
                    </span>
                    <div className="flex w-full gap-3 flex-wrap">
                      <Button
                        variant="brand-primary"
                        iconRight={<FeatherPlus />}
                        onClick={() => handleCreateAlert('COLLECTION_SALE')}
                      >
                        Sale Alert
                      </Button>
                      <Button
                        variant="brand-primary"
                        iconRight={<FeatherPlus />}
                        onClick={() => handleCreateAlert('COLLECTION_NEW_LISTING')}
                      >
                        New Listing Alert
                      </Button>
                      <Button
                        variant="brand-primary"
                        iconRight={<FeatherPlus />}
                        onClick={() => handleCreateAlert('COLLECTION_MINT')}
                      >
                        Mint Alert
                      </Button>
                      <Button
                        variant="brand-primary"
                        iconRight={<FeatherPlus />}
                        onClick={() => handleCreateAlert('COLLECTION_NEW_FLOOR')}
                      >
                        Floor Dropped Alert
                      </Button>
                    </div>
                    <span className="text-caption font-caption text-subtext-color">
                      Choose an alert type to get notified about specific events in this collection
                    </span>
                  </div>

                  {/* Existing Alerts */}
                  <div className="flex w-full flex-col items-start gap-3">
                    <span className="text-label font-label text-default-font">
                      Your Alerts
                    </span>
                    {collection?.id && (
                      <AlertConfigList
                        collectionId={collection.id}
                        onCreateAlert={handleCreateAlert}
                        onEditAlert={handleEditAlert}
                      />
                    )}
                  </div>
                </>
              ) : (
                /* Sign-in prompt for logged-out users */
                <div className="flex w-full flex-col items-center gap-6 py-8">
                  <svg
                    width="64"
                    height="64"
                    viewBox="0 0 64 64"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="32" cy="32" r="31" fill="rgb(7, 48, 59)" stroke="rgb(7, 56, 68)" strokeWidth="2" />
                    <path
                      d="M32 44c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V26c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C27.64 27.36 26 29.92 26 33v5l-2 2v1h16v-1l-2-2z"
                      fill="rgb(5, 162, 194)"
                    />
                  </svg>
                  <span className="text-heading-3 font-heading-3 text-default-font text-center">
                    Sign in to create alerts
                  </span>
                  <span className="text-body font-body text-subtext-color text-left">
                    Get notified when new NFTs are listed, floor prices change, or sales occur in this collection. Customize each alert type with trait filters and price thresholds.
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Traits Tab Content */}
          {activeTab === 'traits' && !collection.skip_trait_stats && (
            <div className="flex w-full flex-col items-start gap-6">
              {/* Rarity Tier Filter */}
              {collection?.trait_stats && Object.keys(collection.trait_stats).length > 0 && (
                <div className="flex w-full items-center gap-3 flex-wrap">
                  <span className="text-body font-body text-subtext-color">Filter by rarity:</span>
                  {(['legendary', 'epic', 'rare', 'uncommon', 'common'] as const).map((tier) => {
                    const tierInfo = {
                      legendary: { label: 'Legendary', color: '#fbbf24' },
                      epic: { label: 'Epic', color: '#a855f7' },
                      rare: { label: 'Rare', color: '#3b82f6' },
                      uncommon: { label: 'Uncommon', color: '#22c55e' },
                      common: { label: 'Common', color: '#9ca3af' },
                    }[tier];
                    const isSelected = selectedRarityTiers.has(tier);
                    return (
                      <button
                        key={tier}
                        onClick={() => {
                          setSelectedRarityTiers(prev => {
                            const newSet = new Set(prev);
                            if (newSet.has(tier)) {
                              newSet.delete(tier);
                            } else {
                              newSet.add(tier);
                            }
                            return newSet;
                          });
                        }}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                          isSelected ? 'border-2' : 'border opacity-60 hover:opacity-100'
                        }`}
                        style={{
                          backgroundColor: isSelected ? `${tierInfo.color}20` : 'transparent',
                          borderColor: tierInfo.color,
                          color: tierInfo.color,
                        }}
                      >
                        {tierInfo.label}
                      </button>
                    );
                  })}
                  {selectedRarityTiers.size > 0 && (
                    <button
                      onClick={() => setSelectedRarityTiers(new Set())}
                      className="text-caption text-subtext-color hover:text-default-font underline"
                    >
                      Clear
                    </button>
                  )}
                </div>
              )}

              {traitThumbnailsLoading ? (
                <div className="flex w-full items-center justify-center py-12">
                  <LoadingSpinner size="md" />
                </div>
              ) : !collection?.trait_stats || Object.keys(collection.trait_stats).length === 0 ? (
                <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-default-background p-12 mobile:p-6">
                  <span className="text-body font-body text-subtext-color">
                    No trait data available for this collection
                  </span>
                </div>
              ) : (
                <div className="flex w-full flex-col gap-8">
                  {/* Sort trait types alphabetically */}
                  {Object.entries(collection.trait_stats)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([traitType, values]) => {
                      // Collect all percentages in this category for relative ranking
                      const allPercentages = Object.values(values).map(v => v.percentage);

                      // Sort values by percentage (rarity) - lowest percentage (rarest) first
                      // Then filter by selected rarity tiers
                      const sortedAndFilteredValues = Object.entries(values)
                        .sort(([, a], [, b]) => a.percentage - b.percentage)
                        .filter(([, stats]) => {
                          if (selectedRarityTiers.size === 0) return true;
                          const tierInfo = getRarityInfoInCategory(stats.percentage, allPercentages);
                          return selectedRarityTiers.has(tierInfo.tier);
                        });

                      // Skip trait type if no values match filter
                      if (sortedAndFilteredValues.length === 0) return null;

                      const valueCount = sortedAndFilteredValues.length;
                      const totalCount = Object.keys(values).length;

                      return (
                        <div key={traitType} className="flex w-full flex-col gap-4">
                          {/* Trait Type Header */}
                          <div className="flex items-center gap-2">
                            <span className="text-heading-3 font-heading-3 text-default-font">
                              {traitType}
                            </span>
                            <span className="text-body font-body text-subtext-color">
                              ({valueCount}{selectedRarityTiers.size > 0 ? ` of ${totalCount}` : ''})
                            </span>
                          </div>

                          {/* Trait Values Grid */}
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                            {sortedAndFilteredValues.map(([value, stats]) => {
                              const rarityInfo = getRarityInfoInCategory(stats.percentage, allPercentages);
                              const thumbnail = traitThumbnails[traitType]?.[value];
                              const imageUrl = thumbnail?.thumbnailUrl || thumbnail?.imageUrl || '';
                              const floorPrice = thumbnail?.floorPrice;

                              // Create the filter URL for navigating to NFTs tab
                              const traitFilter = {
                                [traitType]: [value]
                              };
                              const filterUrl = `/collections/${collection.id}?tab=nfts&traits=${encodeURIComponent(JSON.stringify(traitFilter))}&sort=rarity-low-high`;

                              return (
                                <Link
                                  key={value}
                                  to={filterUrl}
                                  className="flex flex-col rounded-lg border border-solid border-neutral-border bg-default-background overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                                >
                                  {/* Thumbnail */}
                                  <div className="relative aspect-square w-full bg-neutral-100">
                                    {imageUrl ? (
                                      <img
                                        src={imageUrl}
                                        alt={`${traitType}: ${value}`}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                      />
                                    ) : (
                                      <div className="flex h-full w-full items-center justify-center">
                                        <span className="text-3xl text-neutral-400">?</span>
                                      </div>
                                    )}
                                    {/* Rarity Badge */}
                                    <div
                                      className="absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-semibold border"
                                      style={{
                                        backgroundColor: 'rgba(0, 0, 0, 0.85)',
                                        color: rarityInfo.color,
                                        borderColor: `${rarityInfo.color}60`,
                                      }}
                                    >
                                      <span className={rarityInfo.tier === 'legendary' ? 'legendary-shimmer' : ''}>
                                        {rarityInfo.label}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Trait Info */}
                                  <div className="flex flex-col gap-1 p-3">
                                    <span
                                      className={`text-body-bold font-body-bold truncate ${
                                        rarityInfo.tier === 'legendary' ? 'legendary-shimmer' : ''
                                      }`}
                                      style={rarityInfo.tier !== 'legendary' ? { color: rarityInfo.color } : {}}
                                      title={String(value)}
                                    >
                                      {value}
                                    </span>
                                    <div className="flex items-center justify-between gap-2">
                                      <span className="text-caption font-caption text-subtext-color">
                                        {stats.percentage.toFixed(1)}%
                                      </span>
                                      <span className="text-caption font-caption text-subtext-color">
                                        {stats.count.toLocaleString()} items
                                      </span>
                                    </div>
                                    {/* Floor Price */}
                                    {floorPrice && (
                                      <div className="flex items-center gap-1 mt-1">
                                        <span className="text-caption font-caption text-subtext-color">Floor:</span>
                                        <span className="text-caption-bold font-caption-bold text-brand-600">
                                          {formatPriceRounded(floorPrice)}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }).filter(Boolean)}
                </div>
              )}
            </div>
          )}

          {/* Chat Tab Content */}
          {activeTab === 'chat' && collection && (
            <div className="w-full">
              <CollectionChat
                collectionId={collection.id}
                isActive={activeTab === 'chat'}
                isHeaderCollapsed={isHeaderCollapsed}
              />
            </div>
          )}

          {/* Compose Tab Content */}
          {activeTab === 'compose' && collection && featureFlags.composable && collection.activeLaunch?.collection_type === 'composable' && (
            <div className="flex w-full flex-col items-start gap-6">
              <div className="w-full text-center py-8 space-y-4">
                <div className="w-16 h-16 bg-warning-100 rounded-full flex items-center justify-center mx-auto text-[2rem]">
                  <FeatherZap className="text-warning-600" />
                </div>
                <h3 className="text-heading-3 font-heading-3 text-default-font">
                  Build Your Own
                </h3>
                <p className="text-body font-body text-subtext-color max-w-xl mx-auto">
                  Combine your trait NFTs to create a unique new NFT.
                  <br />
                  Select 2+ trait NFTs from different categories to build.
                </p>
                {collection.activeLaunch?.composition_fee_per_nft_drops ? (
                  <p className="text-caption font-caption text-subtext-color">
                    Build fee: {collection.activeLaunch.composition_fee_per_nft_drops / 1_000_000} XRP per NFT burned
                  </p>
                ) : null}
                <div className="pt-2 flex justify-center">
                  <Link to={`/collections/${id}/compose`}>
                    <Button
                      variant="brand-primary"
                      size="large"
                      icon={<FeatherZap />}
                    >
                      Start Building
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Buy Now Transaction Modal */}
      {showTransactionModal && transactionFees && transactionNFTDetails && (
        <XamanTransactionModal
          type="buy"
          nft={transactionNFTDetails}
          fees={transactionFees}
          stage={transactionStage}
          onStageChange={setTransactionStage}
          payload={xamanPayload || undefined}
          txHash={txHash}
          onConfirm={handleConfirmTransaction}
          onSuccess={handleTransactionSuccess}
          onError={handleTransactionError}
          onClose={handleCloseTransactionModal}
        />
      )}

      {/* Floor Price History Modal */}
      {collection && (
        <FloorPriceHistoryModal
          isOpen={showFloorPriceModal}
          onClose={() => setShowFloorPriceModal(false)}
          collectionId={collection.id}
          collectionName={collection.name}
          collectionSize={collection.total_supply}
          hasRarity={collection.rarity_methodology !== 'NONE'}
        />
      )}

      {/* Points Distribution Modal */}
      {collection && collection.has_points_system && (
        <PointsDistributionModal
          isOpen={showPointsDistribution}
          onClose={() => setShowPointsDistribution(false)}
          collectionId={collection.id}
          collectionName={collection.name}
          preloaded={pointsDistribution}
        />
      )}
      
      {/* Alert Configuration Modal */}
      {showAlertModal && selectedAlertType && collection && (
        <AlertConfigModal
          isOpen={showAlertModal}
          onClose={handleCloseAlertModal}
          collectionId={collection.id}
          collectionName={collection.name}
          notificationType={selectedAlertType}
          availableTraits={
            collection.trait_stats
              ? Object.entries(collection.trait_stats).reduce((acc, [traitType, values]) => {
                  acc[traitType] = Object.keys(values);
                  return acc;
                }, {} as { [traitType: string]: string[] })
              : {}
          }
          traitStats={collection.trait_stats}
          existingConfig={editingAlertConfig || undefined}
        />
      )}

      {/* Accept Collection Offer Modal */}
      {showAcceptOfferModal && selectedOfferToAccept && collection && !loadingUserNfts && (
        <AcceptCollectionOfferModal
          isOpen={showAcceptOfferModal}
          onClose={() => {
            setShowAcceptOfferModal(false);
            setSelectedOfferToAccept(null);
          }}
          offer={selectedOfferToAccept}
          userNfts={userNftsForAccept.map(nft => ({
            nftTokenId: nft.nft_token_id,
            name: nft.name,
            imageUrl: nft.image_url,
            thumbnailUrl: nft.thumbnail_url,
            rarityRank: nft.rarity_rank,
            totalSupply: collection.total_supply,
            traits: nft.traits,
          }))}
          collectionName={collection.name}
          onSuccess={handleAcceptOfferSuccess}
        />
      )}

      {/* Sweep Progress Modal */}
      <SweepProgressModal
        open={showSweepProgress}
        onClose={handleSweepComplete}
        nfts={selectedForSweep as any}
        state={sweepPurchase.state}
        totals={sweepPurchase.totals}
        onStartPurchase={sweepPurchase.startPurchasing}
        onSignSuccess={sweepPurchase.handleSignSuccess}
        onSignRejected={sweepPurchase.handleSignRejected}
        onCancelRemaining={sweepPurchase.cancelRemaining}
        onCancelConfirmation={sweepPurchase.cancelConfirmation}
        onComplete={handleSweepComplete}
      />

    </DefaultPageLayout>
  );
};
